import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from '../common/Loader.js'
import Alert from '../common/Alert.js'
import Button from '../common/Button.js'
import { shipmentService } from '../../services/shipmentService.js'
import shipmentEditRequestService from '../../services/shipmentEditRequestService.js'
import { formatHubBranchLabel, filterBranchLocations, filterShipmentLocations } from '../../utils/destinationType.js'
import { handleSuggestionDropdownKeyDown } from '../../utils/suggestionDropdown.js'

const PARTY_INPUTS = [
  { name: 'sender_name', label: 'Sender name' },
  { name: 'sender_address', label: 'Sender address' },
  { name: 'sender_contact', label: 'Sender contact' },
  { name: 'sender_email', label: 'Sender email', type: 'email' },
  { name: 'receiver_name', label: 'Receiver name' },
  { name: 'receiver_address', label: 'Receiver address' },
  { name: 'receiver_contact', label: 'Receiver contact' },
  { name: 'receiver_email', label: 'Receiver email', type: 'email' },
]

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'

const AgentRequestShipmentEdit = () => {
  const { shipmentId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [allowedFields, setAllowedFields] = useState([])
  const [form, setForm] = useState({})
  const [remark, setRemark] = useState('')
  const [consigneeLocations, setConsigneeLocations] = useState([])
  const [showDestinationPicker, setShowDestinationPicker] = useState(false)
  const [destinationSearch, setDestinationSearch] = useState('')
  const [destinationHighlight, setDestinationHighlight] = useState(-1)
  const destinationRef = useRef(null)

  const canChangeDestination = useMemo(
    () => allowedFields.includes('receiver_location') || allowedFields.includes('consignee_location'),
    [allowedFields],
  )

  const filteredDestinations = useMemo(() => {
    const term = destinationSearch.trim().toLowerCase()
    if (!term) return consigneeLocations
    return consigneeLocations.filter((loc) => formatHubBranchLabel(loc).toLowerCase().includes(term))
  }, [consigneeLocations, destinationSearch])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const [allowedRes, hubsRes] = await Promise.all([
        shipmentEditRequestService.allowedFields(shipmentId),
        shipmentService.getAllLocationHubs().catch(() => ({ data: { hubs: [] } })),
      ])
      const payload = allowedRes.data?.data || {}
      const allowed = payload.allowed_fields || []
      if (!allowed.length) {
        Alert.error('This shipment cannot be updated')
        navigate(`/shipments/view/${encodeURIComponent(shipmentId)}`)
        return
      }
      setStatus(payload.status || '')
      setAllowedFields(allowed)
      setForm(payload.current_snapshot || {})
      const hubs = filterBranchLocations(filterShipmentLocations(hubsRes.data?.hubs || []))
      setConsigneeLocations(hubs)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not load shipment')
      navigate('/shipments')
    } finally {
      setLoading(false)
    }
  }, [navigate, shipmentId])

  useEffect(() => {
    load()
  }, [load])

  const onFieldChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const buildProposed = () => {
    const proposed = {}
    allowedFields.forEach((key) => {
      if (key in form) proposed[key] = form[key]
    })
    return proposed
  }

  const submit = async () => {
    try {
      setSaving(true)
      const response = await shipmentEditRequestService.create({
        shipment_id: shipmentId,
        proposed_snapshot: buildProposed(),
        remark,
      })
      Alert.success(response.data?.message || 'Request sent')
      navigate('/shipment-edit-requests')
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not send request')
    } finally {
      setSaving(false)
    }
  }

  const statusHint = (() => {
    const key = String(status || '').toLowerCase()
    if (key === 'booked') {
      return 'You can update party details and the to destination. From branch and amounts stay unchanged until approved.'
    }
    if (['dispatched', 'received', 'in transit'].includes(key)) {
      return 'Only sender/receiver contact details can be changed. Branch and amounts stay unchanged until approved.'
    }
    return 'Changes apply only after admin or accountant approval. Amounts are not part of this request.'
  })()

  if (loading) {
    return (
      <DashboardLayout>
        <Loader message="Loading shipment..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-4 sm:py-6">
        <button
          type="button"
          onClick={() => navigate(`/shipments/view/${encodeURIComponent(shipmentId)}`)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to shipment
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Request shipment update</h1>
        <p className="text-sm text-gray-600 mt-1 break-all">{shipmentId}</p>
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mt-3">{statusHint}</p>

        {canChangeDestination ? (
          <div className="mt-6 relative" ref={destinationRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">To destination</label>
            <div
              role="button"
              tabIndex={0}
              onClick={() => { setShowDestinationPicker(true); setDestinationHighlight(-1) }}
              className={`${inputClass} cursor-pointer min-h-[42px] flex items-center`}
            >
              {form.consignee_location || form.receiver_location || (
                <span className="text-gray-400">Select destination branch</span>
              )}
            </div>
            {showDestinationPicker ? (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg">
                <div className="p-2 border-b border-gray-200">
                  <input
                    type="text"
                    autoFocus
                    value={destinationSearch}
                    onChange={(e) => { setDestinationSearch(e.target.value); setDestinationHighlight(-1) }}
                    onKeyDown={(e) => handleSuggestionDropdownKeyDown(e, {
                      isOpen: showDestinationPicker,
                      options: filteredDestinations,
                      highlightIndex: destinationHighlight,
                      setHighlightIndex: setDestinationHighlight,
                      onClose: () => { setShowDestinationPicker(false); setDestinationSearch('') },
                      onSelect: (loc) => {
                        const label = formatHubBranchLabel(loc)
                        setForm((prev) => ({
                          ...prev,
                          consignee_location: label,
                          receiver_location: label,
                          receiver_location_hub_id: loc?.id ?? '',
                        }))
                        setShowDestinationPicker(false)
                        setDestinationSearch('')
                      },
                    })}
                    placeholder="Search branch..."
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl"
                  />
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {filteredDestinations.map((loc, idx) => (
                    <button
                      key={loc.hub_id || loc.id}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-0 ${idx === destinationHighlight ? 'bg-orange-50' : 'hover:bg-orange-50'}`}
                      onClick={() => {
                        const label = formatHubBranchLabel(loc)
                        setForm((prev) => ({
                          ...prev,
                          consignee_location: label,
                          receiver_location: label,
                          receiver_location_hub_id: loc?.id ?? '',
                        }))
                        setShowDestinationPicker(false)
                        setDestinationSearch('')
                      }}
                    >
                      {formatHubBranchLabel(loc)}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PARTY_INPUTS.filter(({ name }) => allowedFields.includes(name)).map(({ name, label, type }) => (
            <div key={name} className={name.includes('address') ? 'sm:col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              {name.includes('address') ? (
                <textarea
                  rows={2}
                  value={form[name] ?? ''}
                  onChange={(e) => onFieldChange(name, e.target.value)}
                  className={inputClass}
                />
              ) : (
                <input
                  type={type || 'text'}
                  value={form[name] ?? ''}
                  onChange={(e) => onFieldChange(name, e.target.value)}
                  className={inputClass}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Remark for reviewer</label>
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            className={inputClass}
            placeholder="Why this update is needed"
          />
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate(`/shipments/view/${encodeURIComponent(shipmentId)}`)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={saving} disabled={saving}>
            Send request
          </Button>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default AgentRequestShipmentEdit
